import { FieldArray, Form, Formik, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  FiArrowLeft,
  FiPlus,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { AlertMsg } from "../../../shared/components/alert-msg";
import {
  AttributeTemplateFormValues,
  AttributeTemplateItem,
  formValuesToTemplate,
  makeEmptyAttributeTemplateFormAttribute,
  makeEmptyAttributeTemplateFormOption,
  templateToFormValues,
} from "./AttributeTemplateTypes";
import "./AttributeTemplateFormDrawer.scss";

type AttributeTemplateFormDrawerMode = "create" | "edit";

interface DrawerAlert {
  type: "success" | "error";
  message: string;
}

const FieldError = ({ name }: { name: string }) => {
  const { errors, touched, submitCount } = useFormikContext<AttributeTemplateFormValues>();
  const error = getIn(errors, name);
  const isTouched = getIn(touched, name);
  const shouldShow = Boolean(submitCount) || Boolean(isTouched);

  if (!shouldShow || typeof error !== "string" || !error) {
    return null;
  }

  return <div className="error-text">{error}</div>;
};

interface AttributeTemplateFormDrawerProps {
  show: boolean;
  mode: AttributeTemplateFormDrawerMode;
  template: AttributeTemplateItem | null;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (template: AttributeTemplateItem) => void | Promise<void>;
  feedback?: DrawerAlert | null;
  isSubmitting?: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Template name is required"),
  description: Yup.string().trim().required("Description is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"], "Status is required")
    .required("Status is required"),
  attributes: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required("Attribute name is required"),
      isRequired: Yup.boolean().required(),
      options: Yup.array()
        .of(
          Yup.object({
            label: Yup.string().trim().required("Option label is required"),
          }),
        )
        .min(1, "Add at least one option")
        .required("Add at least one option"),
    }),
  ),
});

const createInitialValues = (
  template: AttributeTemplateItem | null,
): AttributeTemplateFormValues =>
  template ? templateToFormValues(template) : {
    name: "",
    description: "",
    status: "active",
    attributes: [],
  };

const AttributeTemplateFormDrawer = ({
  show,
  mode,
  template,
  onClose,
  onBack,
  onSubmit,
  feedback,
  isSubmitting: externalSubmitting = false,
}: AttributeTemplateFormDrawerProps) => {
  if (!show) {
    return null;
  }

  const initialValues = createInitialValues(template);

  return (
    <div className="attribute-template-form-drawer-overlay">
      <div className="attribute-template-form-drawer" role="dialog" aria-modal="true">
        <div className="attribute-template-form-header">
          <div>
            <div className="attribute-template-form-eyebrow">
              {mode === "create" ? "New template" : "Edit template"}
            </div>
            <h3>
              {mode === "create"
                ? "Create attribute template"
                : "Edit attribute template"}
            </h3>
            <p>
              {mode === "create"
                ? "Capture a reusable attribute set for a catalog category."
                : "Update the name, status, and attributes for this template."}
            </p>
          </div>

          <button
            type="button"
            className="close-button"
            onClick={onClose}
            aria-label="Close attribute template form drawer"
          >
            <FiX size={22} />
          </button>
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, helpers) => {
            try {
              const nextTemplate = formValuesToTemplate(values, template);
              await Promise.resolve(onSubmit(nextTemplate));
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {({ values, isSubmitting, setFieldValue, handleBlur, submitForm }) => {
            const showSubmittingState = isSubmitting || externalSubmitting;

            return (
            <Form className="attribute-template-form-shell">
              {feedback && (
                <div className="attribute-template-form-alert-wrap">
                  <AlertMsg type={feedback.type} message={feedback.message} />
                </div>
              )}
              <div className="attribute-template-form-scroll">
                <div className="attribute-template-form-grid">
                  <div className="attribute-template-form-group">
                    <label htmlFor="name">Template name</label>
                    <input
                      id="name"
                      name="name"
                      value={values.name}
                      onChange={(event) => setFieldValue("name", event.target.value)}
                      onBlur={handleBlur}
                      placeholder="Enter template name"
                      className="attribute-template-form-input"
                    />
                    <FieldError name="name" />
                  </div>

                  <div className="attribute-template-form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={values.description}
                      onChange={(event) =>
                        setFieldValue("description", event.target.value)
                      }
                      onBlur={handleBlur}
                      placeholder="Describe what this attribute template is for"
                      rows={4}
                      className="attribute-template-form-textarea"
                    />
                    <FieldError name="description" />
                  </div>

                  <div className="attribute-template-form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={values.status}
                      onChange={(event) => setFieldValue("status", event.target.value)}
                      onBlur={handleBlur}
                      className="attribute-template-form-select"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <FieldError name="status" />
                  </div>
                </div>

                <FieldArray name="attributes">
                  {({ push, remove }) => (
                    <div className="attribute-template-form-section">
                      <div className="attribute-template-form-section-heading">
                        <div>
                          <h4>Attributes</h4>
                          <p>List the fields that should be available in this template.</p>
                        </div>
                        <span>{values.attributes.length} attribute(s)</span>
                      </div>

                      {values.attributes.length === 0 ? null : (
                        <div className="attribute-template-form-attribute-list">
                          {values.attributes.map((attribute, attributeIndex) => (
                            <article
                              className="attribute-template-form-attribute-card"
                              key={attribute.id}
                            >
                              <div className="attribute-template-form-attribute-top">
                                <div className="attribute-template-form-group compact">
                                  <label htmlFor={`attributes.${attributeIndex}.name`}>
                                    Attribute name
                                  </label>
                                  <input
                                    id={`attributes.${attributeIndex}.name`}
                                    name={`attributes.${attributeIndex}.name`}
                                    value={attribute.name}
                                    onChange={(event) =>
                                      setFieldValue(
                                        `attributes.${attributeIndex}.name`,
                                        event.target.value,
                                      )
                                    }
                                    onBlur={handleBlur}
                                    placeholder="Enter attribute name"
                                    className="attribute-template-form-input"
                                  />
                                  <FieldError name={`attributes.${attributeIndex}.name`} />
                                </div>

                                <div className="attribute-template-form-group compact">
                                  <label htmlFor={`attributes.${attributeIndex}.isRequired`}>
                                    Requirement
                                  </label>
                                  <select
                                    id={`attributes.${attributeIndex}.isRequired`}
                                    name={`attributes.${attributeIndex}.isRequired`}
                                    value={attribute.isRequired ? "required" : "optional"}
                                    onChange={(event) =>
                                      setFieldValue(
                                        `attributes.${attributeIndex}.isRequired`,
                                        event.target.value === "required",
                                      )
                                    }
                                    onBlur={handleBlur}
                                    className="attribute-template-form-select"
                                  >
                                    <option value="required">Required</option>
                                    <option value="optional">Optional</option>
                                  </select>
                                </div>
                              </div>

                              <FieldArray name={`attributes.${attributeIndex}.options`}>
                                {({ push: pushOption, remove: removeOption }) => (
                                  <div className="attribute-template-form-options">
                                    <div className="attribute-template-form-options-header">
                                      <span>Options</span>
                                      <button
                                        type="button"
                                        className="attribute-template-form-link-btn"
                                        onClick={() =>
                                          pushOption(makeEmptyAttributeTemplateFormOption())
                                        }
                                      >
                                        <FiPlus size={14} />
                                        Add option
                                      </button>
                                    </div>

                                    <div className="attribute-template-form-option-list">
                                      {attribute.options.map((option, optionIndex) => (
                                        <div
                                          className="attribute-template-form-option-row"
                                          key={option.id}
                                        >
                                          <div className="attribute-template-form-group compact option">
                                            <label htmlFor={`attributes.${attributeIndex}.options.${optionIndex}.label`}>
                                              Option label
                                            </label>
                                            <input
                                              id={`attributes.${attributeIndex}.options.${optionIndex}.label`}
                                              name={`attributes.${attributeIndex}.options.${optionIndex}.label`}
                                              value={option.label}
                                              onChange={(event) =>
                                                setFieldValue(
                                                  `attributes.${attributeIndex}.options.${optionIndex}.label`,
                                                  event.target.value,
                                                )
                                              }
                                              onBlur={handleBlur}
                                              placeholder="Enter option label"
                                              className="attribute-template-form-input"
                                            />
                                            <FieldError
                                              name={`attributes.${attributeIndex}.options.${optionIndex}.label`}
                                            />
                                          </div>

                                          <button
                                            type="button"
                                            className="attribute-template-form-icon-btn"
                                            onClick={() => removeOption(optionIndex)}
                                            aria-label={`Remove ${option.label || "option"}`}
                                          >
                                            <FiTrash2 size={14} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>

                                    <FieldError
                                      name={`attributes.${attributeIndex}.options`}
                                    />
                                  </div>
                                )}
                              </FieldArray>

                              <button
                                type="button"
                                className="attribute-template-form-remove-btn"
                                onClick={() => remove(attributeIndex)}
                              >
                                <FiTrash2 size={14} />
                                Remove attribute
                              </button>
                            </article>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        className="attribute-template-form-add-btn"
                        onClick={() => push(makeEmptyAttributeTemplateFormAttribute())}
                      >
                        <FiPlus size={16} />
                        Add attribute
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              <div className="attribute-template-form-footer">
                <button
                  type="button"
                  className="attribute-template-form-primary-btn"
                  disabled={showSubmittingState}
                  onClick={submitForm}
                >
                  {showSubmittingState && (
                    <span
                      className="attribute-template-form-btn-loader"
                      aria-hidden="true"
                    />
                  )}
                  <span>
                    {showSubmittingState
                      ? mode === "create"
                        ? "Creating..."
                        : "Saving..."
                      : mode === "create"
                        ? "Create attribute template"
                        : "Save changes"}
                  </span>
                </button>
                <button
                  type="button"
                  className="attribute-template-form-secondary-btn"
                  onClick={mode === "edit" ? onBack : onClose}
                >
                  <FiArrowLeft size={16} />
                  {mode === "edit" ? "Back" : "Cancel"}
                </button>
              </div>
            </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default AttributeTemplateFormDrawer;
